import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { useQuickCreate } from "@/lib/quickCreateContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Building2, Search, Plus, Loader2, Users, Filter, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, ChevronLeft, ChevronRight, Link2, Trash2, CheckCircle2, Clock, XCircle, Copy } from "lucide-react";
import { api, Account, Contact, User, fetchWithAuth } from "@/lib/api";
import { SearchableSelect } from "@/components/custom/SearchableSelect";
import { MakeClientInactiveDialog } from "@/components/custom/MakeClientInactiveDialog";
import { portalApi, PortalInvite } from "@/lib/portalApi";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "wouter";
import { useFilterPresets, PRESETS_KEY_ACCOUNTS } from "@/lib/filterPresets";
import { FilterPresetBar } from "@/components/custom/FilterPresetBar";
import {
  loadPersistedAccountsView, serializeAccountsView, savePersistedAccountsView,
  DEFAULT_STATUS_FILTER, DEFAULT_GROUP_BY,
} from "@/lib/accountsViewPersistence";

function CopyBtn({ value }: { value: string }) {
  const { toast } = useToast();
  return (
    <button
      className="text-slate-500 hover:text-foreground transition-colors shrink-0 ml-1 p-0.5 rounded hover:bg-muted align-middle"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(value); toast({ title: "Copied!" }); }}
      title="Copy to clipboard"
    >
      <Copy className="w-4 h-4" />
    </button>
  );
}

interface AccountsFilters {
  searchQuery: string;
  statusFilter: string;
  assigneeFilter: string;
  workflowRoleFilter: string;
  groupFilter: string | null;
}

const ITEMS_PER_PAGE = 15;

// staff see as status badge while managers can toggle
function AccountStatusPill({ account, canChange, onToggle }: {
  account: Account;
  canChange: boolean;
  onToggle: (account: Account) => void;
}) {
  const tone = account.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600";
  const label = account.is_active ? "Active" : "Inactive";
  if (!canChange) {
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${tone}`} data-testid={`badge-status-${account.id}`}>
        {label}
      </span>
    );
  }
  const hover = account.is_active ? "hover:bg-green-200" : "hover:bg-gray-200";
  return (
    <button
      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${tone} ${hover}`}
      title={account.is_active ? "Make this client inactive" : "Reactivate this client"}
      onClick={() => onToggle(account)}
      data-testid={`badge-status-${account.id}`}
    >
      {label}
    </button>
  );
}

export default function Accounts() {
  // saves filters besides search text and page number
  const [persisted] = useState(loadPersistedAccountsView);
  const [searchQuery, setSearchQuery] = useState("");
  // select's onValueChange and the saved preset shape both hand back one
  const [statusFilter, setStatusFilter] = useState<string>(persisted.statusFilter ?? DEFAULT_STATUS_FILTER);
  const [assigneeFilter, setAssigneeFilter] = useState(persisted.assigneeFilter ?? "all");
  const [workflowRoleFilter, setWorkflowRoleFilter] = useState(persisted.workflowRoleFilter ?? "all");
  const [groupFilter, setGroupFilter] = useState<string | null>(persisted.groupFilter ?? null);
  const [groupPopoverOpen, setGroupPopoverOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [groupBy, setGroupBy] = useState<"none" | "status" | "assignee" | "workflow_role">(
    persisted.groupBy ?? DEFAULT_GROUP_BY
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [sortCol, setSortCol] = useState<"client" | "status" | null>(persisted.sortCol ?? null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(persisted.sortDir ?? "asc");

  const hasActiveFilters = searchQuery !== "" || statusFilter !== "active" || assigneeFilter !== "all" || workflowRoleFilter !== "all" || groupFilter !== null;
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("active");
    setAssigneeFilter("all");
    setWorkflowRoleFilter("all");
    setGroupFilter(null);
    setCurrentPage(1);
  };

  // remember the view
  const persistedViewJson = useMemo(
    () => serializeAccountsView({
      statusFilter: statusFilter as "active" | "inactive" | "all",
      assigneeFilter, workflowRoleFilter, groupFilter, groupBy, sortCol, sortDir,
    }),
    [statusFilter, assigneeFilter, workflowRoleFilter, groupFilter, groupBy, sortCol, sortDir]
  );
  useEffect(() => {
    if (persistedViewJson) savePersistedAccountsView(persistedViewJson);
  }, [persistedViewJson]);

  // Filter presets
  const { presets: filterPresets, save: savePreset, remove: removePreset } = useFilterPresets<AccountsFilters>(PRESETS_KEY_ACCOUNTS);
  const currentFilters = useMemo<AccountsFilters>(() => ({
    searchQuery, statusFilter, assigneeFilter, workflowRoleFilter, groupFilter,
  }), [searchQuery, statusFilter, assigneeFilter, workflowRoleFilter, groupFilter]);
  function applyFilters(f: AccountsFilters) {
    setSearchQuery(f.searchQuery);
    setStatusFilter(f.statusFilter);
    setAssigneeFilter(f.assigneeFilter);
    setWorkflowRoleFilter(f.workflowRoleFilter);
    setGroupFilter(f.groupFilter);
    setCurrentPage(1);
  }
  const [dialogOpen, setDialogOpen] = useState(false);
  const { trigger, setTrigger } = useQuickCreate();
  useEffect(() => {
    if (trigger === "new-client") { setDialogOpen(true); setTrigger(null); }
  }, [trigger]);
  const [newAccountName, setNewAccountName] = useState("");
  const [newSmartsuiteId, setNewSmartsuiteId] = useState("");
  const [portalDialogAccount, setPortalDialogAccount] = useState<Account | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState<string | null>(null);
  const [pendingInactiveAccount, setPendingInactiveAccount] = useState<Account | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // also enforced on server
  const canChangeActiveStatus = user?.role === "manager" || user?.role === "admin";
  const [, navigate] = useLocation();

  const showInactive = statusFilter === "all" || statusFilter === "inactive";
  
  const { data: accounts = [], isLoading } = useQuery<Account[]>({
    queryKey: ["accounts", showInactive],
    queryFn: () => api.getAccounts(showInactive),
  });

  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["contacts", "all", "active"],
    queryFn: () => api.getContacts({ status: "active" }),
  });

  const { data: allUsers = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => api.getUsers().catch(() => []),
  });

  const { data: allAccountMembers = [] } = useQuery<{ account_id: string; user_id: string; workflow_role?: string | null }[]>({
    queryKey: ["account-members-bulk"],
    queryFn: () => api.getAllAccountMembers(),
  });

  const { data: clientGroups = [], isSuccess: clientGroupsLoaded } = useQuery<{ id: string; name: string; client_count: number }[]>({
    queryKey: ["client-groups"],
    queryFn: () => fetchWithAuth("/api/client-groups"),
  });

  // a remembered group filter can outlive its group
  useEffect(() => {
    if (!groupFilter || !clientGroupsLoaded) return;
    if (!clientGroups.some((g) => g.id === groupFilter)) {
      setGroupFilter(null);
      setCurrentPage(1);
    }
  }, [groupFilter, clientGroupsLoaded, clientGroups]);

  const { data: groupMembers = [] } = useQuery<{ account_id: string }[]>({
    queryKey: ["client-group-members", groupFilter],
    queryFn: () => fetchWithAuth(`/api/client-groups/${groupFilter}/members`),
    enabled: !!groupFilter,
  });

  const groupMemberIds = useMemo(
    () => new Set(groupMembers.map((m: any) => m.account_id)),
    [groupMembers]
  );

  const membersByAccount = useMemo(() => {
    const map = new Map<string, Set<string>>();
    allAccountMembers.forEach(({ account_id, user_id }) => {
      if (!map.has(account_id)) map.set(account_id, new Set());
      map.get(account_id)!.add(user_id);
    });
    return map;
  }, [allAccountMembers]);

  // account_id → Set of workflow_roles assigned on that account
  const workflowRolesByAccount = useMemo(() => {
    const map = new Map<string, Set<string>>();
    allAccountMembers.forEach(({ account_id, workflow_role }) => {
      if (!workflow_role) return;
      if (!map.has(account_id)) map.set(account_id, new Set());
      map.get(account_id)!.add(workflow_role);
    });
    return map;
  }, [allAccountMembers]);

  const primaryContactByAccount = useMemo(() => {
    const contactMap: Record<string, Contact> = {};
    contacts.forEach((contact) => {
      if (contact.account_id) {
        const existing = contactMap[contact.account_id];
        if (!existing || (!existing.is_primary && contact.is_primary)) {
          contactMap[contact.account_id] = contact;
        }
      }
    });
    return contactMap;
  }, [contacts]);

  const createAccountMutation = useMutation({
    mutationFn: (data: { account_name: string; smartsuite_record_id?: string }) => api.createAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast({ title: "Client created successfully" });
      setDialogOpen(false);
      setNewAccountName("");
      setNewSmartsuiteId("");
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => api.updateAccount(id, { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast({ title: "Client updated" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const { data: portalInvites = [], refetch: refetchInvites } = useQuery<PortalInvite[]>({
    queryKey: ["portal-invites", portalDialogAccount?.id],
    queryFn: () => portalApi.listInvites(portalDialogAccount!.id),
    enabled: !!portalDialogAccount,
  });

  const { data: recentPortalActivity = [] } = useQuery<{ account_id: string; created_at: string }[]>({
    queryKey: ["portal-activity-accounts"],
    queryFn: async () => {
      const items = await fetchWithAuth<{ account_id: string; created_at: string; read?: boolean }[]>("/api/portal-activity");
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      // requires migration 099
      return items.filter(i => !i.read && new Date(i.created_at).getTime() > cutoff);
    },
    staleTime: 5 * 60 * 1000,
  });
  const activePortalAccountIds = new Set(recentPortalActivity.map(i => i.account_id));

  const createInviteMutation = useMutation({
    mutationFn: () => portalApi.createInvite(portalDialogAccount!.id, inviteEmail.trim()),
    onSuccess: (data) => {
      setGeneratedInviteUrl(data.invite_url);
      setInviteEmail("");
      refetchInvites();
      toast({ title: "Invite link generated" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (id: string) => portalApi.revokeInvite(id),
    onSuccess: () => {
      refetchInvites();
      toast({ title: "Invite revoked" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteInviteMutation = useMutation({
    mutationFn: (id: string) => portalApi.deleteInvite(id),
    onSuccess: () => {
      refetchInvites();
      toast({ title: "Invite deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleToggleActive = (account: Account) => {
    if (account.is_active) {
      // confirmation for making inactive
      setPendingInactiveAccount(account);
    } else {
      // Reactivating — safe, do it directly
      toggleActiveMutation.mutate({ id: account.id, isActive: true });
    }
  };

  const handleCreateAccount = () => {
    if (!newAccountName.trim()) {
      toast({ title: "Error", description: "Client name is required", variant: "destructive" });
      return;
    }
    createAccountMutation.mutate({
      account_name: newAccountName.trim(),
      smartsuite_record_id: newSmartsuiteId.trim() || undefined,
    });
  };

  const filteredAccounts = useMemo(() => {
    let filtered = accounts;

    if (statusFilter === "active") {
      filtered = filtered.filter((a) => a.is_active);
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter((a) => !a.is_active);
    }

    if (assigneeFilter === "unassigned") {
      filtered = filtered.filter((a) => !membersByAccount.has(a.id) || membersByAccount.get(a.id)!.size === 0);
    } else if (assigneeFilter !== "all") {
      filtered = filtered.filter((a) => membersByAccount.get(a.id)?.has(assigneeFilter) ?? false);
    }

    if (workflowRoleFilter !== "all") {
      filtered = filtered.filter((a) => workflowRolesByAccount.get(a.id)?.has(workflowRoleFilter) ?? false);
    }

    if (groupFilter && groupMemberIds.size > 0) {
      filtered = filtered.filter((a) => groupMemberIds.has(a.id));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((account) => {
        const contact = primaryContactByAccount[account.id];
        return (
          (account.account_name ?? "").toLowerCase().includes(query) ||
          (contact?.name && contact.name.toLowerCase().includes(query)) ||
          (contact?.email && contact.email.toLowerCase().includes(query)) ||
          (contact?.phone && contact.phone.includes(query))
        );
      });
    }

    return filtered;
  }, [accounts, searchQuery, statusFilter, assigneeFilter, workflowRoleFilter, groupFilter, groupMemberIds, membersByAccount, workflowRolesByAccount, primaryContactByAccount]);

  const sortedAccounts = useMemo(() => {
    if (!sortCol) return filteredAccounts;
    return [...filteredAccounts].sort((a, b) => {
      let cmp = 0;
      if (sortCol === "client") cmp = (a.account_name ?? "").localeCompare(b.account_name ?? "");
      else if (sortCol === "status") cmp = (a.is_active ? 0 : 1) - (b.is_active ? 0 : 1);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredAccounts, sortCol, sortDir]);

  const groupedAccounts = useMemo(() => {
    if (groupBy === "none") return [] as { key: string; label: string; accounts: Account[] }[];
    const map = new Map<string, { key: string; label: string; accounts: Account[] }>();

    for (const account of sortedAccounts) {
      let entries: { key: string; label: string }[] = [];

      if (groupBy === "status") {
        const k = account.is_active ? "active" : "inactive";
        entries = [{ key: k, label: account.is_active ? "Active" : "Inactive" }];
      } else if (groupBy === "assignee") {
        const ids = Array.from(membersByAccount.get(account.id) ?? []);
        if (ids.length === 0) {
          entries = [{ key: "__unassigned__", label: "Unassigned" }];
        } else {
          entries = ids.map((uid) => {
            const u = allUsers.find((x) => x.id === uid);
            return { key: uid, label: u?.name ?? u?.email ?? "Unknown" };
          });
        }
      } else {
        // workflow_role
        const roles = Array.from(workflowRolesByAccount.get(account.id) ?? []);
        if (roles.length === 0) {
          entries = [{ key: "__unassigned__", label: "Unassigned" }];
        } else {
          const roleLabels: Record<string, string> = {
            account_manager: "Account Manager",
            preparer: "Preparer",
            checker: "Checker",
            reviewer: "Reviewer",
            assembler: "Assembler",
            signer: "Signer",
          };
          entries = roles.map((r) => ({ key: r, label: roleLabels[r] ?? r }));
        }
      }

      for (const { key, label } of entries) {
        if (!map.has(key)) map.set(key, { key, label, accounts: [] });
        map.get(key)!.accounts.push(account);
      }
    }

    const groups = Array.from(map.values());
    if (groupBy === "status") {
      groups.sort((a) => (a.key === "active" ? -1 : 1));
    } else {
      groups.sort((a, b) => {
        if (a.key === "__unassigned__") return 1;
        if (b.key === "__unassigned__") return -1;
        return a.label.localeCompare(b.label);
      });
    }
    return groups;
  }, [groupBy, sortedAccounts, membersByAccount, workflowRolesByAccount, allUsers]);

  const totalPages = Math.ceil(sortedAccounts.length / ITEMS_PER_PAGE);
  const paginatedAccounts = sortedAccounts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold" data-testid="text-page-title">Clients</h1>
            <p className="text-sm text-muted-foreground">{filteredAccounts.length} Clients</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/client-groups")} data-testid="button-groups">
              <Users className="w-4 h-4 mr-2" />
              Group
            </Button>
            <Button onClick={() => setDialogOpen(true)} data-testid="button-add-account" className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>
        </div>

        {/* Search and Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by any field (name, email, phone, etc)"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="pl-9"
              data-testid="input-search-accounts"
            />
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-auto min-w-[100px] h-9" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>

            <SearchableSelect
              value={assigneeFilter}
              onValueChange={(v) => { setAssigneeFilter(v); setCurrentPage(1); }}
              options={[
                { value: "all", label: "All Assignees" },
                { value: "unassigned", label: "Unassigned" },
                ...allUsers.map((u) => ({ value: u.id, label: u.name || u.email })),
              ]}
              placeholder="All Assignees"
              searchPlaceholder="Search assignees..."
              className="min-w-[140px]"
              data-testid="filter-assignee"
            />

            <Select value={workflowRoleFilter} onValueChange={(v) => { setWorkflowRoleFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-auto min-w-[140px] h-9" data-testid="filter-workflow-role">
                <SelectValue placeholder="Workflow Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="account_manager">Account Manager</SelectItem>
                <SelectItem value="preparer">Preparer</SelectItem>
                <SelectItem value="checker">Checker</SelectItem>
                <SelectItem value="reviewer">Reviewer</SelectItem>
                <SelectItem value="assembler">Assembler</SelectItem>
                <SelectItem value="signer">Signer</SelectItem>
              </SelectContent>
            </Select>

            <Select value={groupBy} onValueChange={(v: typeof groupBy) => { setGroupBy(v); setExpandedGroups(new Set()); setCurrentPage(1); }}>
              <SelectTrigger className="h-9 w-auto min-w-[120px]" data-testid="select-group-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Grouping</SelectItem>
                <SelectItem value="status">By Status</SelectItem>
                <SelectItem value="assignee">By Assignee</SelectItem>
                <SelectItem value="workflow_role">By Workflow Role</SelectItem>
              </SelectContent>
            </Select>

            <Popover open={groupPopoverOpen} onOpenChange={setGroupPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={groupFilter ? "default" : "outline"}
                  size="sm"
                  className="h-9"
                  data-testid="filter-group"
                >
                  <Filter className="w-3 h-3 mr-1" />
                  {groupFilter
                    ? (clientGroups.find((g) => g.id === groupFilter)?.name ?? "Group")
                    : "Filter by Group"}
                  <span className="ml-1">▾</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-1" align="start">
                {clientGroups.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-2">No groups found</div>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    {groupFilter && (
                      <button
                        className="w-full text-left text-xs text-muted-foreground px-2 py-1.5 hover:bg-muted rounded"
                        onClick={() => { setGroupFilter(null); setGroupPopoverOpen(false); setCurrentPage(1); }}
                      >
                        ✕ Clear group filter
                      </button>
                    )}
                    {clientGroups.map((g) => (
                      <button
                        key={g.id}
                        className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted flex justify-between items-center ${groupFilter === g.id ? "bg-muted font-medium" : ""}`}
                        onClick={() => { setGroupFilter(g.id); setGroupPopoverOpen(false); setCurrentPage(1); }}
                      >
                        <span>{g.name}</span>
                        <span className="text-xs text-muted-foreground">{g.client_count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <FilterPresetBar
              presets={filterPresets}
              currentFilters={currentFilters}
              hasActiveFilters={hasActiveFilters}
              onApply={applyFilters}
              onSave={savePreset}
              onRemove={removePreset}
            />

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => queryClient.invalidateQueries({ queryKey: ["accounts"] })}
              data-testid="button-refresh"
              title="Refresh client list"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="border rounded-lg bg-card">
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Building2 className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No clients found</p>
              <p className="text-sm">
                {searchQuery ? "Try a different search term" : "Click 'Add Client' to create your first client"}
              </p>
            </div>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden bg-card">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_80px_1fr_140px_100px_1fr] gap-2 px-4 py-3 bg-muted/50 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <button
                className={`flex items-center gap-1 cursor-pointer text-left uppercase tracking-wide font-medium ${sortCol === "client" ? "text-foreground" : "text-muted-foreground"}`}
                onClick={() => {
                  if (sortCol === "client") setSortDir((d) => d === "asc" ? "desc" : "asc");
                  else { setSortCol("client"); setSortDir("asc"); }
                }}
              >
                Client {sortCol === "client" ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-50" />}
              </button>
              <button
                className={`flex items-center gap-1 cursor-pointer text-left uppercase tracking-wide font-medium ${sortCol === "status" ? "text-foreground" : "text-muted-foreground"}`}
                onClick={() => {
                  if (sortCol === "status") setSortDir((d) => d === "asc" ? "desc" : "asc");
                  else { setSortCol("status"); setSortDir("asc"); }
                }}
              >
                Status {sortCol === "status" ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-50" />}
              </button>
              <div className="flex items-center gap-1">
                Contact <ArrowUpDown className="w-3 h-3" />
              </div>
              <div>Phone</div>
              <div className="flex items-center gap-1">
                Transactions <ArrowUpDown className="w-3 h-3" />
              </div>
              <div>SmartVault Uploads</div>
            </div>

            {/* Table Body */}
            <div className="divide-y">
              {groupBy !== "none"
                ? groupedAccounts.map((group) => {
                    const isCollapsed = !expandedGroups.has(group.key);
                    return (
                      <div key={group.key}>
                        {/* Group header row */}
                        <div
                          className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => setExpandedGroups((prev) => {
                            const n = new Set(prev);
                            n.has(group.key) ? n.delete(group.key) : n.add(group.key);
                            return n;
                          })}
                        >
                          <div className="flex items-center">
                            <ChevronRight className={`w-4 h-4 text-foreground/60 transition-transform duration-150 ${isCollapsed ? "" : "rotate-90"}`} />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{group.label}</span>
                            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground border">
                              {group.accounts.length}
                            </span>
                          </div>
                        </div>
                        {/* Group rows */}
                        {!isCollapsed && group.accounts.map((account) => {
                const contact = primaryContactByAccount[account.id];
                const transactionCount = 0;
                const smartVaultUrl = account.smartsuite_record_id && !account.smartsuite_record_id.startsWith("manual_")
                  ? `https://sypportal001.smartvault.com/app/Carpenter%2/${encodeURIComponent(account.account_name)}/Client%20Upload`
                  : null;
                
                return (
                  <div
                    key={account.id}
                    className="grid grid-cols-[1fr_80px_1fr_140px_100px_1fr] gap-2 px-4 py-3 items-center hover:bg-muted/30 transition-colors"
                    data-testid={`row-account-${account.id}`}
                  >
                    {/* Client Name */}
                    <div className="flex items-center gap-2">
                      <a
                        href={`/accounts/${account.id}`}
                        className="text-primary hover:underline font-medium"
                        data-testid={`link-client-${account.id}`}
                      >
                        {account.account_name}
                      </a>
                      {activePortalAccountIds.has(account.id) && (
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 shrink-0" title="Recent client portal activity (last 7 days)" />
                      )}
                      <CopyBtn value={account.account_name} />
                      <button
                        title="Portal invites"
                        className="text-muted-foreground hover:text-primary transition-colors"
                        onClick={(e) => { e.stopPropagation(); setPortalDialogAccount(account); setGeneratedInviteUrl(null); setInviteEmail(""); }}
                      >
                        <Link2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Status */}
                    <div>
                      <AccountStatusPill account={account} canChange={canChangeActiveStatus} onToggle={handleToggleActive} />
                    </div>

                    {/* Contact */}
                    <div className="text-sm text-muted-foreground flex items-center">
                      {contact?.name || "-"}
                      {contact?.name && <CopyBtn value={contact.name} />}
                    </div>

                    {/* Phone */}
                    <div className="text-sm text-muted-foreground flex items-center">
                      {contact?.phone || "-"}
                      {contact?.phone && <CopyBtn value={contact.phone} />}
                    </div>

                    {/* Transactions */}
                    <div className="text-sm text-muted-foreground text-center">
                      {transactionCount}
                    </div>

                    {/* SmartVault Uploads */}
                    <div className="text-sm text-muted-foreground flex items-center gap-1 min-w-0">
                      {smartVaultUrl ? (
                        <>
                          <a
                            href={smartVaultUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline truncate"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {smartVaultUrl}
                          </a>
                          <CopyBtn value={smartVaultUrl} />
                        </>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                );
              })}
                      </div>
                    );
                  })
                : paginatedAccounts.map((account) => {
                const contact = primaryContactByAccount[account.id];
                const transactionCount = 0;
                const smartVaultUrl = account.smartsuite_record_id && !account.smartsuite_record_id.startsWith("manual_")
                  ? `https://sypportal001.smartvault.com/app/Carpenter%2/${encodeURIComponent(account.account_name)}/Client%20Upload`
                  : null;
                return (
                  <div
                    key={account.id}
                    className="grid grid-cols-[1fr_80px_1fr_140px_100px_1fr] gap-2 px-4 py-3 items-center hover:bg-muted/30 transition-colors"
                    data-testid={`row-account-${account.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <a href={`/accounts/${account.id}`} className="text-primary hover:underline font-medium" data-testid={`link-client-${account.id}`}>
                        {account.account_name}
                      </a>
                      {activePortalAccountIds.has(account.id) && (
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 shrink-0" title="Recent client portal activity (last 7 days)" />
                      )}
                      <CopyBtn value={account.account_name} />
                      <button title="Portal invites" className="text-muted-foreground hover:text-primary transition-colors"
                        onClick={(e) => { e.stopPropagation(); setPortalDialogAccount(account); setGeneratedInviteUrl(null); setInviteEmail(""); }}>
                        <Link2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div>
                      <AccountStatusPill account={account} canChange={canChangeActiveStatus} onToggle={handleToggleActive} />
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center">
                      {contact?.name || "-"}
                      {contact?.name && <CopyBtn value={contact.name} />}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center">
                      {contact?.phone || "-"}
                      {contact?.phone && <CopyBtn value={contact.phone} />}
                    </div>
                    <div className="text-sm text-muted-foreground text-center">{transactionCount}</div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1 min-w-0">
                      {smartVaultUrl ? (
                        <>
                          <a href={smartVaultUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate" onClick={(e) => e.stopPropagation()}>{smartVaultUrl}</a>
                          <CopyBtn value={smartVaultUrl} />
                        </>
                      ) : "-"}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination — hidden when grouped */}
            {groupBy === "none" && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  data-testid="button-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                
                <Select 
                  value={currentPage.toString()} 
                  onValueChange={(v) => setCurrentPage(parseInt(v))}
                >
                  <SelectTrigger className="w-16 h-8" data-testid="select-page">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="button-next-page"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Portal Invites Dialog */}
        <Dialog open={!!portalDialogAccount} onOpenChange={(open) => { if (!open) { setPortalDialogAccount(null); setGeneratedInviteUrl(null); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Client Portal Invites
              </DialogTitle>
              <DialogDescription>
                {portalDialogAccount?.account_name} — Send a sign-in link to give this client portal access.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Invite form */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="client@email.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && inviteEmail.trim() && createInviteMutation.mutate()}
                />
                <Button
                  onClick={() => createInviteMutation.mutate()}
                  disabled={!inviteEmail.trim() || createInviteMutation.isPending}
                >
                  {createInviteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Link"}
                </Button>
              </div>

              {/* Generated invite URL */}
              {generatedInviteUrl && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">Copy this link and paste it into an email to your client. It can only be used once.</p>
                  <div className="flex gap-2">
                    <Input readOnly value={generatedInviteUrl} className="text-xs font-mono" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { navigator.clipboard.writeText(generatedInviteUrl); toast({ title: "Link copied" }); }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              )}

              {/* Existing invites */}
              {portalInvites.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Existing Invites</p>
                  <div className="space-y-1">
                    {portalInvites.map((invite) => (
                      <div key={invite.id} className="flex items-center justify-between gap-2 text-sm py-1">
                        <div className="flex items-center gap-2 min-w-0">
                          {invite.accepted_at && !invite.revoked_at ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                          ) : invite.revoked_at ? (
                            <XCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          ) : (
                            <Clock className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
                          )}
                          <span className="truncate">{invite.email}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {invite.revoked_at ? "Revoked" : invite.accepted_at ? "Accepted" : "Pending"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {!invite.revoked_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                              onClick={() => revokeInviteMutation.mutate(invite.id)}
                              disabled={revokeInviteMutation.isPending || deleteInviteMutation.isPending}
                            >
                              Revoke
                            </Button>
                          )}
                          <button
                            className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                            onClick={() => deleteInviteMutation.mutate(invite.id)}
                            disabled={deleteInviteMutation.isPending || revokeInviteMutation.isPending}
                            title="Delete invite"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Client</DialogTitle>
              <DialogDescription>
                Create a new client account for time tracking.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="accountName">Client Name *</Label>
                <Input
                  id="accountName"
                  placeholder="Enter client name"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  data-testid="input-account-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smartsuiteId">SmartSuite Record ID (optional)</Label>
                <Input
                  id="smartsuiteId"
                  placeholder="Enter SmartSuite record ID"
                  value={newSmartsuiteId}
                  onChange={(e) => setNewSmartsuiteId(e.target.value)}
                  data-testid="input-smartsuite-id"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateAccount} 
                disabled={createAccountMutation.isPending}
                data-testid="button-submit-account"
              >
                {createAccountMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      {/* make inactive confirmation dialong */}
      <MakeClientInactiveDialog
        account={pendingInactiveAccount}
        onClose={() => setPendingInactiveAccount(null)}
      />

      </div>
    </Layout>
  );
}
